import usePagination from "@mui/material/usePagination";
import { styled } from "@mui/material/styles";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import { Menu, MenuItem, Stack, Typography, useTheme } from "@mui/material";
import { useState, useContext, useMemo } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SquareButton from "../../../ui/square-button";
import { OnChangeFn, PaginationState } from "@tanstack/react-table";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import { userHasPermission } from "@/modules/common/utils";
import { NawyPassportContext } from "@/modules/common/context/nawy-passport.provider";
import { groupMappings } from "@/modules/leads/constants";

const List = styled("ul")({
  listStyle: "none",
  padding: 0,
  margin: 0,
  display: "flex",
  gap: "4px",
});

export default function DataTablePagination({
  rowsCount,
  calculatedPageCount,
  pagination,
  setPagination,
  pageSizeOptions,
  startContent,
}: {
  rowsCount: number;
  calculatedPageCount: number;
  pagination: PaginationState;
  setPagination: OnChangeFn<PaginationState>;
  pageSizeOptions: number[];
  /** Rendered right after the "Showing: … results" cluster. */
  startContent?: React.ReactNode;
}) {
  const theme = useTheme();
  const { isMobile } = useScreenSize();
  const { permissions: currentUserPermissions } =
    useContext(NawyPassportContext);

  // Filter page size options based on user permissions
  const filteredPageSizeOptions = useMemo(() => {
    const baseOptions = [10, 20, 50];
    const permissionBasedOptions: number[] = [];

    // Check permissions for higher page sizes
    Object.entries(groupMappings).forEach(([pageSize, permissions]) => {
      const hasPermission = permissions.every((permission: any) =>
        userHasPermission(permission, currentUserPermissions)
      );
      if (hasPermission) {
        permissionBasedOptions.push(parseInt(pageSize));
      }
    });

    // Filter the passed pageSizeOptions to only include those the user has permission for
    const allowedOptions = [...baseOptions, ...permissionBasedOptions];
    return pageSizeOptions.filter((option) => allowedOptions.includes(option));
  }, [pageSizeOptions, currentUserPermissions]);

  const { items } = usePagination({
    count: calculatedPageCount,
    page: pagination.pageIndex + 1,
    siblingCount: isMobile ? 0 : 1,
    boundaryCount: isMobile ? 0 : 1,
    onChange: (_, page) => {
      setPagination((prev) => {
        return {
          ...prev,
          pageIndex: page - 1,
        };
      });
    },
  });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const textStyle = isMobile
    ? {
        ...theme.nawy.typography.english.variants.body.sm.regular,
        color: theme.nawy.colors.semantic.text.secondary,
      }
    : {
        ...theme.nawy.typography.english.variants.body.md.regular,
        color: theme.nawy.colors.semantic.text.primary,
      };

  const mobileItems = isMobile
    ? (() => {
        const currentPage = pagination.pageIndex + 1;
        const mobileItemsList = [];

        // Previous button
        if (pagination.pageIndex > 0) {
          mobileItemsList.push({
            type: "previous" as const,
            onClick: () =>
              setPagination((prev) => ({
                ...prev,
                pageIndex: prev.pageIndex - 1,
              })),
          });
        }

        if (currentPage > 1 && calculatedPageCount > 1) {
          mobileItemsList.push({
            type: "page" as const,
            page: currentPage - 1,
            selected: false,
            onClick: () =>
              setPagination((prev) => ({
                ...prev,
                pageIndex: currentPage - 2,
              })),
          });
        }

        mobileItemsList.push({
          type: "page" as const,
          page: currentPage,
          selected: true,
          onClick: () => {},
        });

        if (currentPage < calculatedPageCount) {
          mobileItemsList.push({
            type: "page" as const,
            page: currentPage + 1,
            selected: false,
            onClick: () =>
              setPagination((prev) => ({ ...prev, pageIndex: currentPage })),
          });
        }

        if (pagination.pageIndex < calculatedPageCount - 1) {
          mobileItemsList.push({
            type: "next" as const,
            onClick: () =>
              setPagination((prev) => ({
                ...prev,
                pageIndex: prev.pageIndex + 1,
              })),
          });
        }

        return mobileItemsList;
      })()
    : [];

  return (
    <div
      style={{
        gap: "8px",
        display: "flex",
        alignItems: "center",
        flexDirection: isMobile ? "column" : "row",
        justifyContent: isMobile ? "center" : "space-between",
        width: "100%",
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        width="100%"
        alignItems="center"
        justifyContent={isMobile ? "space-between" : "flex-start"}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          {isMobile ? (
            <>
              <SquareButton
                title="Rows per page"
                onClick={handleMenuClick}
                variant="outlined"
                sx={{
                  minWidth: 64,
                  px: 1,
                  width: "fit-content",
                  fontSize: 14,
                  fontWeight: 400,
                  lineHeight: "20px",
                  color: theme.nawy.colors.semantic.text.primary,
                }}
              >
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography>{pagination.pageSize}</Typography>
                  <ExpandMoreIcon sx={{ width: 16, height: 16 }} />
                </Stack>
              </SquareButton>

              <Typography
                sx={{
                  ...theme.nawy.typography.english.variants.body.sm.regular,
                  color: theme.nawy.colors.semantic.text.secondary,
                }}
              >
                of {rowsCount} results
              </Typography>
            </>
          ) : (
            <>
              <Typography sx={textStyle}>Showing:</Typography>

              <SquareButton
                title="Rows per page"
                onClick={handleMenuClick}
                variant="outlined"
                sx={{
                  minWidth: 64,
                  px: 1,
                  width: "fit-content",
                  fontSize: 14,
                  fontWeight: 400,
                  lineHeight: "20px",
                  color: theme.nawy.colors.semantic.text.primary,
                }}
              >
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography>{pagination.pageSize}</Typography>
                  <ExpandMoreIcon sx={{ width: 16, height: 16 }} />
                </Stack>
              </SquareButton>

              <Typography sx={textStyle}>of {rowsCount} results</Typography>
            </>
          )}
        </Stack>

        {startContent}

        {isMobile && calculatedPageCount > 1 && (
          <nav>
            <List>
              {mobileItems.map((item, index) => {
                let children = null;

                if (item.type === "page") {
                  children = (
                    <SquareButton
                      variant="outlined"
                      sx={{
                        border: item.selected ? "1px solid" : "none",
                        color: item.selected ? "#1B1B1D" : "#313131",
                        borderColor: item.selected ? "#015C9A" : "none",
                        backgroundColor: item.selected ? "#E6EFF5" : undefined,
                        minWidth: "32px",
                        px: 0.5,
                        width: "fit-content",
                        fontSize: "12px",
                        fontWeight: item.selected ? 600 : 400,
                        lineHeight: "18px",
                        letterSpacing: "0%",
                      }}
                      onClick={item.onClick}
                    >
                      {item.page}
                    </SquareButton>
                  );
                } else if (item.type === "previous") {
                  children = (
                    <SquareButton
                      variant="ghost"
                      onClick={item.onClick}
                      sx={{
                        minWidth: "32px",
                        px: 0.5,
                      }}
                    >
                      <KeyboardArrowLeft sx={{ fontSize: 20 }} />
                    </SquareButton>
                  );
                } else if (item.type === "next") {
                  children = (
                    <SquareButton
                      variant="ghost"
                      onClick={item.onClick}
                      sx={{
                        minWidth: "32px",
                        px: 0.5,
                      }}
                    >
                      <KeyboardArrowRight sx={{ fontSize: 20 }} />
                    </SquareButton>
                  );
                }

                return <li key={index}>{children}</li>;
              })}
            </List>
          </nav>
        )}

        <Menu
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
          sx={{ "& .MuiPaper-root": { borderRadius: 2 } }}
        >
          {filteredPageSizeOptions.map((option) => (
            <MenuItem
              key={option}
              selected={option === pagination.pageSize}
              sx={{
                color: theme.nawy.colors.semantic.text.secondary,
                fontSize: 14,
                fontWeight: 600,
              }}
              onClick={() => {
                handleMenuClose();
                setPagination({ pageSize: option, pageIndex: 0 });
              }}
            >
              {option}
            </MenuItem>
          ))}
        </Menu>
      </Stack>

      {!isMobile && (
        <nav>
          <List>
            {items.map(({ page, type, selected, ...item }, index) => {
              let children = null;

              if (type === "start-ellipsis" || type === "end-ellipsis") {
                const jumpPages = type === "start-ellipsis" ? -5 : 5;
                const targetPage = Math.max(
                  1,
                  Math.min(
                    calculatedPageCount,
                    pagination.pageIndex + 1 + jumpPages
                  )
                );

                children = (
                  <SquareButton
                    variant="outlined"
                    sx={{ borderColor: "#DCE4E9", color: "#313131" }}
                    onClick={() => {
                      setPagination((prev) => ({
                        ...prev,
                        pageIndex: targetPage - 1,
                      }));
                    }}
                  >
                    &hellip;
                  </SquareButton>
                );
              } else if (type === "page") {
                children = (
                  <SquareButton
                    variant="outlined"
                    sx={{
                      border: selected ? "1px solid" : "none",
                      color: selected ? "#1B1B1D" : "#313131",
                      borderColor: selected ? "#015C9A" : "none",
                      backgroundColor: selected ? "#E6EFF5" : undefined,
                      minWidth: "36px",
                      px: 1,
                      width: "fit-content",
                      fontSize: "14px",
                      fontWeight: selected ? 600 : 400,
                      lineHeight: "20px",
                      letterSpacing: "0%",
                    }}
                    {...item}
                  >
                    {page}
                  </SquareButton>
                );
              } else if (type === "previous" && pagination.pageIndex > 0) {
                children = (
                  <SquareButton variant="ghost" {...item}>
                    <KeyboardArrowLeft />
                  </SquareButton>
                );
              } else if (
                type === "next" &&
                pagination.pageIndex < calculatedPageCount - 1
              ) {
                children = (
                  <SquareButton variant="ghost" {...item}>
                    <KeyboardArrowRight />
                  </SquareButton>
                );
              }

              return <li key={index}>{children}</li>;
            })}
          </List>
        </nav>
      )}
    </div>
  );
}
